import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useAwaitControl } from 'react-redux-await-control';

import { Alert, Button, Popover, Table, TableColumns, Typography } from '~/ui/components';
import { useDrawer } from '~/hooks/useDrawer';
import { getTicketDetails, getTicketSupportService } from '~/store/tickets/actions';
import Conversation from '~/ui/components/Conversation/Conversation';
import moment from 'moment';
import ConciergeConversation from '~/ui/components/ConciergeConversation/ConciergeConversation';
import ServiceResume from '../ServiceResume/ServiceResume';
import ServiceExpandedRow from '../ServiceExpandedRow/ServiceExpandedRow';
import Services from '../../drawers/Services/Services';
import { Wrapper, TopSection, MessageButton, Code, DateContent, CommentsWrapper } from './ServiceHistory.styles';

interface ServiceHistoryProps {
  id: string;
}

function ServiceHistory({ id }: ServiceHistoryProps) {
  const { openDrawer } = useDrawer();

  const getTicketDetailsControl = useAwaitControl(getTicketDetails);
  const getTicketSupportServiceControl = useAwaitControl(getTicketSupportService);

  const ticketSupportService = getTicketSupportServiceControl.result(id);
  const ticketDetail = getTicketDetailsControl.result(id);

  const isSupportServiceLoading = getTicketSupportServiceControl.isRunning(id);
  const isLoading = getTicketDetailsControl.isRunning(id);

  const disableCreateService = useMemo(
    () => !ticketDetail?.canCreateNewSupport || isLoading || isSupportServiceLoading,
    [ticketDetail, isLoading, isSupportServiceLoading],
  );

  const updatedata = useMemo(
    () =>
      ticketSupportService?.records?.map((record) => ({
        ...record,
        canExpand: true,
      })),
    [ticketSupportService],
  );

  const handleOpenNewService = useCallback(() => {
    openDrawer('new-service', <Services id={id} />, {
      title: 'Novo atendimento',
      closable: true,
      width: 410,
      sideBySide: true,
    });
  }, [id]);

  const renderExpandedRow = (serviceRow) => <ServiceExpandedRow service={serviceRow} />;

  const handleConversation = useCallback((service) => {
    if (service?.twilio?.conversationId) {
      openDrawer(
        'ticket-conversation',
        <CommentsWrapper>
          {service?.twilio?.conversationId.startsWith('CH') && service?.twilio?.conversationId.length === 34 ? (
            <Conversation conversationId={service?.twilio?.conversationId} />
          ) : (
            <ConciergeConversation conversationId={service?.twilio?.conversationId} />
          )}
        </CommentsWrapper>,
        {
          title: (
            <>
              Historico de conversas <small>({service.id})</small>
            </>
          ),
          closable: true,
          width: 410,
          sideBySide: true,
        },
      );
    }
  }, []);

  const renderServiceDate = useCallback(
    (date) => (
      <DateContent>
        <Typography type="caption">{moment(date).format('DD/MM/YY')}</Typography>
        <Typography color="element.secondary" type="bodyXSmall">
          {moment(date).format('HH:mm')}
        </Typography>
      </DateContent>
    ),
    [],
  );

  const columns: TableColumns<any> = [
    {
      accessorKey: 'cod',
      header: () => 'COD.',
      cell: ({ row }) => (
        <Popover
          title="Resumo do atendimento"
          width={480}
          placement="bottom"
          content={<ServiceResume service={row.original} product={ticketDetail?.product} />}
        >
          <Code onClick={row.getToggleExpandedHandler()}>{row.original.id}</Code>
        </Popover>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: () => 'DATA ATEND.',
      cell: ({ row }) => renderServiceDate(row.original.createdAt),
    },
    {
      accessorKey: 'channel',
      header: () => 'CANAL',
      cell: ({ row }) => row.original.channel.name,
    },
    {
      accessorKey: 'team',
      header: () => 'TIME',
      headerAlign: 'center',
      cellAlign: 'center',
      cell: ({ row }) =>
        row.original.team?.children?.length ? row.original.team.children[0].name : row.original.team.name,
    },
    {
      accessorKey: 'messages',
      header: () => 'CONVERSAS',
      headerAlign: 'center',
      cellAlign: 'center',
      cell: ({ row }) => (
        <MessageButton
          icon="Message"
          iconOnly
          variant="text"
          disabled={!row?.original?.twilio?.conversationId || row?.original?.channel?.name === '0800'}
          onClick={() => handleConversation(row?.original)}
        />
      ),
    },
  ];

  useEffect(() => {
    getTicketSupportServiceControl.start({ id }, { actionId: id });
  }, [id]);

  return (
    <Wrapper>
      <TopSection>
        <Button
          variant="outline"
          rounded
          color="primary"
          icon="Add"
          size="sm"
          disabled={disableCreateService}
          onClick={handleOpenNewService}
        >
          Novo atendimento
        </Button>
      </TopSection>
      <Table
        noItemsMessage={<Alert label="Nenhum atendimento encontrado." status="neutral" fullWidth />}
        columns={columns}
        data={updatedata}
        loading={isSupportServiceLoading}
        maxHeight={305}
        isTHeadFixed
        noPadding
        renderExpandedRow={renderExpandedRow}
        responsiveCols={['cod', 'createdAt', 'channel', 'team', 'messages']}
      />
    </Wrapper>
  );
}

export default memo(ServiceHistory);
